import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const CustomerPortal = () => {
    const { orgName } = useParams();
    const [lotInfo, setLotInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    
    // Form State
    const [selectedSpace, setSelectedSpace] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [plateNumber, setPlateNumber] = useState('');
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        const fetchLotInfo = async () => {
            try {
                const res = await axios.get(`/portal/org/${orgName}/info`);
                setLotInfo(res.data);
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.detail || "Could not load lot information.");
                setLoading(false);
            }
        };
        fetchLotInfo();
    }, [orgName]);

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        setError('');
        
        // 1. Simulate Stripe Payment delay
        setTimeout(async () => {
            try {
                // 2. Submit purchase to backend
                await axios.post(`/portal/org/${orgName}/purchase`, {
                    space_id: parseInt(selectedSpace),
                    purchase_type: lotInfo.time_period,
                    phone_number: phoneNumber,
                    plate_number: plateNumber,
                    name: name || null,
                    address: address || null
                });
                
                setSuccess(true);
            } catch (err) {
                setError(err.response?.data?.detail || "Purchase failed. Please try again.");
            } finally {
                setIsProcessing(false);
            }
        }, 2000); // Mock 2 seconds payment processing
    };

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--text-color)' }}>Loading...</div>;
    
    if (error && !lotInfo) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px' }}>
            <div className="glass-card" style={{ padding: '30px', textAlign: 'center', color: 'var(--danger)', maxWidth: '400px' }}>
                <h3>Error</h3>
                <p>{error}</p>
            </div>
        </div>
    );

    if (success) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px', background: 'var(--bg-color)' }}>
            <div className="glass-card" style={{ padding: '40px', textAlign: 'center', maxWidth: '400px', width: '100%' }}>
                <div style={{ fontSize: '48px', color: 'var(--success)', marginBottom: '20px' }}>✓</div>
                <h2 style={{ color: 'var(--text-color)', marginBottom: '10px' }}>Payment Successful!</h2>
                <p style={{ color: 'var(--text-color)', marginBottom: '20px' }}>Your parking space has been reserved.</p>
                <div style={{ padding: '15px', background: 'var(--input-bg)', borderRadius: '8px', marginBottom: '20px', border: '1px solid var(--input-border)' }}>
                    <p style={{ margin: '5px 0', color: 'var(--text-color)' }}><strong>Lot:</strong> {lotInfo.lot_name}</p>
                    <p style={{ margin: '5px 0', color: 'var(--text-color)' }}><strong>Space:</strong> {lotInfo.available_spaces.find(s => s.id === parseInt(selectedSpace))?.space_number}</p>
                    <p style={{ margin: '5px 0', color: 'var(--text-color)' }}><strong>License Plate:</strong> {plateNumber}</p>
                </div>
                <p style={{ color: 'var(--primary)', fontSize: '14px' }}>A receipt has been sent to your phone.</p>
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px', background: 'var(--bg-color)' }}>
            <div className="glass-card" style={{ padding: '30px', maxWidth: '400px', width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <img src="/logo-primary.png" alt="Wagoing Logo" style={{ height: '40px', marginBottom: '15px' }} />
                    <h2 style={{ margin: '0 0 10px 0', color: 'var(--text-color)' }}>{lotInfo.org_name}</h2>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center' }}>
                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary)' }}>${(lotInfo.price_per_spot / 100).toFixed(2)}</span>
                        <span style={{ color: 'var(--text-color)', opacity: 0.8 }}>/ {lotInfo.time_period.toLowerCase()}</span>
                    </div>
                </div>

                {error && <div style={{ color: 'white', backgroundColor: 'var(--danger-hover)', padding: '10px', borderRadius: '8px', marginBottom: '20px', textAlign: 'center' }}>{error}</div>}

                <form onSubmit={handlePaymentSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: 'bold' }}>Select Space</label>
                        <select 
                            value={selectedSpace} 
                            onChange={(e) => setSelectedSpace(e.target.value)} 
                            required
                            style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                        >
                            <option value="" disabled>Choose an available space...</option>
                            {lotInfo.available_spaces.map(space => (
                                <option key={space.id} value={space.id}>
                                    {space.space_number}{space.group_name ? ` (${space.group_name})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: 'bold' }}>License Plate</label>
                        <input 
                            type="text" 
                            placeholder="e.g. ABC-1234" 
                            value={plateNumber} 
                            onChange={(e) => setPlateNumber(e.target.value.toUpperCase())} 
                            required
                            style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: 'bold' }}>Phone Number</label>
                        <input 
                            type="tel" 
                            placeholder="(555) 555-5555" 
                            value={phoneNumber} 
                            onChange={(e) => setPhoneNumber(e.target.value)} 
                            required
                            style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                        />
                    </div>

                    {lotInfo.time_period === 'MONTHLY' && (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: 'bold' }}>Full Name</label>
                                <input 
                                    type="text" 
                                    placeholder="John Doe" 
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                    required
                                    style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: 'bold' }}>Billing Address</label>
                                <input 
                                    type="text" 
                                    placeholder="123 Main St, City, ST 12345" 
                                    value={address} 
                                    onChange={(e) => setAddress(e.target.value)} 
                                    required
                                    style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' }}
                                />
                            </div>
                        </>
                    )}

                    <div style={{ marginTop: '20px', padding: '15px', borderRadius: '8px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #6772e5, #00c4ff)' }}></div>
                        <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-color)' }}>Payment Details</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <input 
                                type="text" 
                                placeholder="Card Number" 
                                value="**** **** **** 4242"
                                disabled
                                style={{ padding: '10px', borderRadius: '4px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', opacity: 0.7 }}
                            />
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input 
                                    type="text" 
                                    placeholder="MM/YY" 
                                    value="12/25"
                                    disabled
                                    style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', opacity: 0.7 }}
                                />
                                <input 
                                    type="text" 
                                    placeholder="CVC" 
                                    value="123"
                                    disabled
                                    style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', opacity: 0.7 }}
                                />
                            </div>
                            <p style={{ margin: 0, fontSize: '12px', color: 'var(--primary)', textAlign: 'center', marginTop: '5px' }}>
                                Mock payment enabled. Your card will not be charged.
                            </p>
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        className="btn btn-primary" 
                        disabled={isProcessing}
                        style={{ marginTop: '10px', padding: '15px', fontSize: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                        {isProcessing ? (
                            <span>Processing...</span>
                        ) : (
                            <span>Pay ${(lotInfo.price_per_spot / 100).toFixed(2)}</span>
                        )}
                    </button>
                    
                    <p style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-color)', opacity: 0.6, marginTop: '10px' }}>
                        Powered by <strong>Stripe</strong>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default CustomerPortal;
